import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Stack, Chip, List, ListItem, ListItemAvatar, ListItemText, Avatar,
  Divider, InputAdornment, IconButton, Paper, Grid, Collapse, Tabs, Tab,
  Alert, useTheme, useMediaQuery, Drawer, AppBar, Toolbar, Fab,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import HomeIcon from '@mui/icons-material/Home';
import AddIcon from '@mui/icons-material/Add';
import BusinessIcon from '@mui/icons-material/Business';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import EditIcon from '@mui/icons-material/Edit';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import BrandLogo from '../../components/BrandLogo';
import PropertyList from './components/PropertyList';
import PropertyDetail from './components/PropertyDetail';
import PropertyForm from './components/PropertyForm';
import PropertyImport from './components/PropertyImport';
import MarketAnalysis from './components/MarketAnalysis';
import ShowingManager from './components/ShowingManager';
import MediaManager from './components/MediaManager';
import PROPERTY_API from '../../api/property.api.js';

const statusColors = {
  Active: 'success',
  Pending: 'warning',
  Sold: 'default',
  OffMarket: 'error',
};

function PropertiesPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [properties, setProperties] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [tab, setTab] = useState(0);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const recognitionRef = useRef(null);

  // Fetch properties from backend
  useEffect(() => {
    loadProperties();
  }, []);

  const loadProperties = async () => {
    setLoading(true);
    try {
      const data = await PROPERTY_API.getProperties();
      console.log('Properties loaded from API:', data); // Debug log
      setProperties(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching properties:', err);
      setError('Failed to load properties. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filteredProperties = (properties || []).filter((p) => (p.address || '').toLowerCase().includes(search.toLowerCase())
    || (p.type || '').toLowerCase().includes(search.toLowerCase())
    || (p.agent || '').toLowerCase().includes(search.toLowerCase())
    || (p.owner || '').toLowerCase().includes(search.toLowerCase())
    || (p.features || []).join(' ').toLowerCase().includes(search.toLowerCase()));

  console.log('Filtered properties being passed to PropertyList:', filteredProperties); // Debug log

  const handleAddProperty = () => setAddDialogOpen(true);
  const handleCloseAddDialog = () => setAddDialogOpen(false);

  const handleSaveProperty = async (propertyData) => {
    try {
      if (propertyData._id) {
        // Update existing property
        await PROPERTY_API.updateProperty(propertyData._id, propertyData);
      } else {
        // Create new property
        await PROPERTY_API.createProperty(propertyData);
      }
      setAddDialogOpen(false);
      setTab(0);
      loadProperties(); // Refresh the list
    } catch (err) {
      console.error('Error saving property:', err);
      setError('Failed to save property. Please try again.');
    }
  };

  const handlePropertyDelete = async (property) => {
    try {
      await PROPERTY_API.deleteProperty(property._id);
      loadProperties(); // Refresh the list
      if (selectedProperty?._id === property._id) {
        setSelectedProperty(null);
      }
    } catch (err) {
      console.error('Error deleting property:', err);
      setError('Failed to delete property. Please try again.');
    }
  };

  const handlePropertySelect = (property) => {
    setSelectedProperty(property);
    if (isMobile) {
      setMobileDrawerOpen(false); // Close drawer on mobile when property is selected
    }
  };

  const handleVoicePrompt = () => {
    if (!('webkitSpeechRecognition' in window)) {
      alert('Speech recognition not supported in this browser.');
      return;
    }
    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
      return;
    }
    const recognition = new window.webkitSpeechRecognition();
    recognition.lang = 'en-US';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.onresult = (event) => {
      const { transcript } = event.results[0][0];
      // Example: "Address 123 Main St, Price 500000, Type Condo"
      const updates = {};
      transcript.split(',').forEach((part) => {
        const [key, ...rest] = part.trim().split(' ');
        const value = rest.join(' ').trim();
        if (/^address$/i.test(key)) updates.address = value;
        if (/^price$/i.test(key)) updates.price = value;
        if (/^type$/i.test(key)) updates.type = value;
        if (/^status$/i.test(key)) updates.status = value;
        // Add more fields as needed
      });
      handleSaveProperty(updates);
    };
    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);
    recognition.start();
    recognitionRef.current = recognition;
    setIsListening(true);
  };

  // Mobile layout
  if (isMobile) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        {/* Mobile App Bar */}
        <AppBar position="static" elevation={1}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => setMobileDrawerOpen(true)}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              <BrandLogo
                size="small"
                orientation="horizontal"
                showTagline={false}
                iconSize={20}
              />
              <Typography variant="h6" sx={{ ml: 2 }}>
                Properties
              </Typography>
            </Box>
            <Button
              color="inherit"
              startIcon={<AddIcon />}
              onClick={handleAddProperty}
            >
              Add
            </Button>
          </Toolbar>
        </AppBar>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ m: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Main Content */}
        <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
          {selectedProperty ? (
            // Property Detail View
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Paper elevation={0} sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Tabs
                  value={tab}
                  onChange={(_, v) => setTab(v)}
                  sx={{ borderBottom: 1, borderColor: 'divider' }}
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  <Tab label="Details" />
                  <Tab label="Edit" />
                  <Tab label="Market Analysis" />
                  <Tab label="Showings" />
                  <Tab label="Media" />
                </Tabs>
                <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                  {tab === 0 && (
                    <PropertyDetail
                      property={selectedProperty}
                      onEdit={(property) => {
                        setSelectedProperty(property);
                        setTab(1);
                      }}
                    />
                  )}
                  {tab === 1 && (
                    <PropertyForm
                      property={selectedProperty}
                      onSave={handleSaveProperty}
                      mode="edit"
                    />
                  )}
                  {tab === 2 && (
                    <MarketAnalysis property={selectedProperty} />
                  )}
                  {tab === 3 && (
                    <ShowingManager property={selectedProperty} />
                  )}
                  {tab === 4 && (
                    <MediaManager property={selectedProperty} />
                  )}
                </Box>
              </Paper>
            </Box>
          ) : (
            // Empty State
            <Box sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              p: 3,
            }}
            >
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Select a Property
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Choose a property from the list to view details
              </Typography>
            </Box>
          )}
        </Box>

        {/* Mobile Drawer for Property List */}
        <Drawer
          anchor="left"
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          sx={{
            '& .MuiDrawer-paper': {
              width: isSmallMobile ? '100%' : 320,
              boxSizing: 'border-box',
            },
          }}
        >
          <Box sx={{
            p: 2, height: '100%', display: 'flex', flexDirection: 'column',
          }}
          >
            <Box sx={{ mb: 2 }}>
              <PropertyImport onImportSuccess={loadProperties} />
            </Box>
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
              <PropertyList
                properties={filteredProperties}
                loading={loading}
                onPropertySelect={handlePropertySelect}
                onPropertyEdit={(property) => {
                  setSelectedProperty(property);
                  setTab(1);
                  setMobileDrawerOpen(false);
                }}
                onPropertyDelete={handlePropertyDelete}
                search={search}
                onSearchChange={setSearch}
              />
            </Box>
          </Box>
        </Drawer>

        {/* Add Property Dialog */}
        <Dialog
          open={addDialogOpen}
          onClose={handleCloseAddDialog}
          maxWidth="md"
          fullWidth
          fullScreen={isSmallMobile}
          PaperProps={{
            sx: {
              borderRadius: isSmallMobile ? 0 : 3,
              p: 0,
            },
          }}
        >
          <DialogTitle sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            py: 2,
            px: 3,
          }}
          >
            <HomeIcon sx={{ fontSize: 32 }} />
            <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
              Add New Property
            </Typography>
            <IconButton onClick={handleCloseAddDialog} sx={{ color: 'primary.contrastText' }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <Box sx={{ p: { xs: 2, sm: 4 } }}>
            <PropertyForm onSave={handleSaveProperty} onCancel={handleCloseAddDialog} mode="add" />
          </Box>
        </Dialog>
      </Box>
    );
  }

  // Desktop layout
  return (
    <Box sx={{
      p: { xs: 1, sm: 2, md: 3 }, height: '100vh', display: 'flex', flexDirection: 'column',
    }}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3} sx={{ flex: 1, minHeight: 0 }}>
        <Grid item xs={12} md={4}>
          <Paper
            elevation={2}
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              p: 2,
              minHeight: 0,
            }}
          >
            <Box sx={{ mb: 2 }}>
              <PropertyImport onImportSuccess={loadProperties} />
            </Box>
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
              <PropertyList
                properties={filteredProperties}
                loading={loading}
                onPropertySelect={handlePropertySelect}
                onPropertyEdit={(property) => {
                  setSelectedProperty(property);
                  setTab(1);
                }}
                onPropertyDelete={handlePropertyDelete}
                search={search}
                onSearchChange={setSearch}
              />
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={8}>
          <Paper
            elevation={2}
            sx={{
              height: '100%',
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}
          >
            <Tabs value={tab} onChange={(_, v) => setTab(v)}>
              <Tab label="Details" />
              <Tab label="Edit" />
              <Tab label="Market Analysis" />
              <Tab label="Showings" />
              <Tab label="Media" />
            </Tabs>
            <Box sx={{ mt: 2, flex: 1, overflow: 'auto' }}>
              {tab === 0 && (
                <PropertyDetail
                  property={selectedProperty}
                  onEdit={(property) => {
                    setSelectedProperty(property);
                    setTab(1);
                  }}
                />
              )}
              {tab === 1 && (
                <PropertyForm
                  property={selectedProperty}
                  onSave={handleSaveProperty}
                  mode="edit"
                />
              )}
              {tab === 2 && (
                <MarketAnalysis property={selectedProperty} />
              )}
              {tab === 3 && (
                <ShowingManager property={selectedProperty} />
              )}
              {tab === 4 && (
                <MediaManager property={selectedProperty} />
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Dialog
        open={addDialogOpen}
        onClose={handleCloseAddDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 0 } }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          py: 2,
          px: 3,
        }}
        >
          <HomeIcon sx={{ fontSize: 32 }} />
          <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
            Add New Property
          </Typography>
          <IconButton onClick={handleCloseAddDialog} sx={{ color: 'primary.contrastText' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Box sx={{ p: { xs: 2, sm: 4 } }}>
          <PropertyForm onSave={handleSaveProperty} onCancel={handleCloseAddDialog} mode="add" />
        </Box>
      </Dialog>
    </Box>
  );
}

export default PropertiesPage;
